import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const inp = "mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]";
const EMPTY = { id: null, title: "", category: "Resources", description: "", url: "", order: 0 };

export function AdminToolbox() {
  const [data, setData] = useState({ categories: [], items: [] });
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const target = useRef(null);

  const load = () => api.get("/toolbox").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const field = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    const { id, ...body } = editing;
    if (!body.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (id) await api.put(`/admin/toolbox/${id}`, { ...body, order: Number(body.order) || 0 });
      else await api.post("/admin/toolbox", { ...body, order: Number(body.order) || data.items.length });
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Remove "${i.title}" from the Tool Box?`)) return;
    await api.delete(`/admin/toolbox/${i.id}`); load();
  };
  const pickFile = (i) => { target.current = i; fileRef.current.click(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const i = target.current; setUploading(i.id);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/toolbox/${i.id}/file`, fd); toast.success(`Uploaded ${f.name}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(null); }
  };
  const removeFile = async (i) => {
    if (!window.confirm(`Remove the file "${i.file_name}" from "${i.title}"?`)) return;
    await api.delete(`/admin/toolbox/${i.id}/file`); load();
  };

  return (
    <div data-testid="admin-toolbox">
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-[#545454]">Items directors see in their Tool Box. Each can have a link, an uploaded file, or both.</p>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#920e12] hover:bg-[#550001] text-white shrink-0" data-testid="toolbox-add-btn"><Plus size={16} className="mr-1" /> Add item</Button>
      </div>
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="toolbox-file-input" />
      <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
            <tr><th className="p-3">Title</th><th className="p-3">Section</th><th className="p-3">Link</th><th className="p-3">File</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.id} className="border-t border-[#dfd8da]" data-testid={`admin-toolbox-row-${i.id}`}>
                <td className="p-3 font-bold text-[#1a1617]">{i.title}</td>
                <td className="p-3">{i.category}</td>
                <td className="p-3 max-w-[14rem] truncate">{i.url ? <a href={i.url} target="_blank" rel="noreferrer" className="text-[#920e12] underline">{i.url}</a> : <span className="text-[#545454]">—</span>}</td>
                <td className="p-3">
                  {i.has_file ? (
                    <span className="inline-flex items-center gap-1">{i.file_name}
                      <button onClick={() => removeFile(i)} aria-label="Remove file" data-testid={`toolbox-remove-file-${i.id}`} className="text-[#991b1b]"><X size={14} /></button></span>
                  ) : "—"}
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => pickFile(i)} disabled={uploading === i.id} data-testid={`toolbox-upload-${i.id}`} className="border-[#920e12] text-[#920e12]">
                      {uploading === i.id ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...i })} data-testid={`toolbox-edit-${i.id}`} className="border-[#920e12] text-[#920e12]"><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(i)} data-testid={`toolbox-delete-${i.id}`} className="border-[#991b1b] text-[#991b1b]"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="toolbox-editor">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit item" : "New Tool Box item"}</DialogTitle>
            <DialogDescription>Paste a Google Sheet, Doc or Form link. Upload files from the table after saving.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label htmlFor="tb-title">Title</Label><input id="tb-title" value={editing.title} onChange={(e) => field("title", e.target.value)} className={inp} data-testid="toolbox-title" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label htmlFor="tb-cat">Section</Label>
                  <select id="tb-cat" value={editing.category} onChange={(e) => field("category", e.target.value)} className={`${inp} bg-white`} data-testid="toolbox-category">
                    {data.categories.map((c) => <option key={c}>{c}</option>)}
                  </select></div>
                <div><Label htmlFor="tb-order">Order</Label><input id="tb-order" type="number" value={editing.order} onChange={(e) => field("order", e.target.value)} className={inp} data-testid="toolbox-order" /></div>
              </div>
              <div><Label htmlFor="tb-desc">Short description</Label><input id="tb-desc" value={editing.description} onChange={(e) => field("description", e.target.value)} className={inp} data-testid="toolbox-description" /></div>
              <div><Label htmlFor="tb-url">Link (optional)</Label><input id="tb-url" value={editing.url} onChange={(e) => field("url", e.target.value)} placeholder="https://docs.google.com/…" className={inp} data-testid="toolbox-url" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#920e12] text-white" data-testid="toolbox-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
